export const WALL_DECOR_OUTPUT_LAYOUT = "wall-decor-sheet";

export const WALL_DECOR_SIZES = [
  {
    value: "small",
    label: "Liten",
    description: "Et lite blikkfang ved seng, hylle eller lekekrok",
    widthMm: 550,
    heightMm: 367,
    spreadLabel: "ca. 75 × 50 cm ferdig på veggen",
    printAreaSqm: 0.2,
    priceDeltaNok: 0,
  },
  {
    value: "medium",
    label: "Medium",
    description: "Vår mest populære størrelse til én veggsone",
    widthMm: 900,
    heightMm: 600,
    spreadLabel: "ca. 120 × 80 cm ferdig på veggen",
    printAreaSqm: 0.54,
    priceDeltaNok: 150,
    popular: true,
  },
  {
    value: "large",
    label: "Stor",
    description: "En tydelig motivverden over seng eller kommode",
    widthMm: 1250,
    heightMm: 833,
    spreadLabel: "ca. 165 × 110 cm ferdig på veggen",
    printAreaSqm: 1.04,
    priceDeltaNok: 350,
  },
  {
    value: "xl",
    label: "XL",
    description: "For en hel fondvegg eller et stort lekeområde",
    widthMm: 1500,
    heightMm: 1000,
    spreadLabel: "ca. 200 × 135 cm ferdig på veggen",
    printAreaSqm: 1.5,
    priceDeltaNok: 600,
  },
];

export const WALL_DECOR_THEMES = [
  theme("forest-friends", "Skogens små venner", "Dyr og natur", 14, "forest-friends", { roomImageUrl: "/wall-decor/hero-forest-room.webp" }),
  theme("space-adventure", "Min egen verdensromferd", "Verdensrommet", 16, "space-adventure", { roomImageUrl: "/wall-decor/room-space-ceiling.webp", ceilingSuitable: true }),
  theme("ocean-friends", "Havets hemmelige venner", "Hav og dyr", 16, "ocean-friends"),
  theme("dinosaur-valley", "Dinosaurdalen", "Dinosaurer", 15, "dinosaur-valley"),
  theme("construction-crew", "Byggegjengen", "Kjøretøy", 15, "construction-crew"),
  theme("dream-clouds", "Drømmeskyer", "Rolige rom", 18, "dream-clouds", { ceilingSuitable: true }),
  theme("flower-meadow", "Blomstereng", "Natur og blomster", 20, "flower-meadow"),
  theme("northern-lights-dream", "Nordlysdrøm", "Arktisk og nordlys", 15, "northern-lights-dream", { ceilingSuitable: true }),
  theme("secret-forest-cabin", "Den hemmelige skogshytta", "Dyr og natur", 18, "secret-forest-cabin"),
  theme("rainbow-safari", "Regnbuesafari", "Dyr og natur", 17, "rainbow-safari"),
  theme("balloon-cloud-journey", "Ballongferd over skyene", "Luft og eventyr", 16, "balloon-cloud-journey", { ceilingSuitable: true }),
  theme("robot-workshop", "Robotverkstedet", "Roboter", 19, "robot-workshop"),
  theme("bee-flower-garden", "Bienes blomsterhage", "Natur og blomster", 22, "bee-flower-garden"),
  theme("arctic-friends", "Arktiske venner", "Arktisk og nordlys", 16, "arctic-friends"),
  theme("moon-rabbits", "Månekaninenes natt", "Rolige rom", 18, "moon-rabbits", { ceilingSuitable: true }),
  theme("magic-fairy-garden", "Den magiske fehagen", "Eventyr", 20, "magic-fairy-garden"),
  theme("woodland-train", "Skogstoget", "Kjøretøy", 17, "woodland-train"),
];

export function resolveWallDecorSize(template, designOrResponses = {}) {
  const options = template?.sizeOptions?.length ? template.sizeOptions : WALL_DECOR_SIZES;
  const responses = designOrResponses.responses || designOrResponses;
  const field = (template?.fields || []).find((item) => item.role === "wallDecorSize");
  const value = responses?.[field?.id] || field?.defaultValue || template?.defaultSizeValue || "medium";
  return options.find((item) => item.value === value) || options[0];
}

export function resolveWallDecorTheme(template, designOrResponses = {}) {
  const responses = designOrResponses.responses || designOrResponses;
  const field = (template?.fields || []).find((item) => item.role === "wallDecorTheme" || item.role === "labelTheme");
  const value = responses?.[field?.id] || field?.defaultValue;
  return field?.options?.find((item) => item.value === value) || field?.options?.[0] || null;
}

export function validateWallDecorTemplate(template) {
  if (template?.outputLayout !== WALL_DECOR_OUTPUT_LAYOUT) return [];
  const issues = [];
  const sizes = template.sizeOptions || [];
  const themes = (template.fields || []).find((field) => field.role === "wallDecorTheme")?.options || [];
  if (!sizes.length) issues.push(issue("sizes_missing", "Legg til minst én størrelse."));
  if (!themes.length) issues.push(issue("themes_missing", "Legg til minst ett veggdekormotiv."));
  for (const size of sizes) {
    if (!size.value || !size.label || Number(size.widthMm) <= 0 || Number(size.heightMm) <= 0) issues.push(issue("size_invalid", `Størrelsen ${size.label || size.value || "uten navn"} er ugyldig.`));
    if (Number(size.widthMm) > 1610) issues.push(issue("size_too_wide", `${size.label} er bredere enn printerens maksimale printbredde på 1610 mm.`));
  }
  const values = new Set();
  for (const motif of themes) {
    if (!motif.value || values.has(motif.value)) issues.push(issue("theme_id_invalid", `Motivet ${motif.label || "uten navn"} mangler en unik ID.`));
    values.add(motif.value);
    if (!motif.label) issues.push(issue("theme_name_missing", "Et motiv mangler navn."));
    if (!motif.thumbnailUrl && !motif.imageUrl) issues.push(issue("theme_preview_missing", `${motif.label || "Motivet"} mangler forhåndsvisning.`));
    if (!motif.productionAssetUrl) issues.push(issue("theme_production_missing", `${motif.label || "Motivet"} mangler produksjonsfil.`));
    if (motif.commercialRightsConfirmed !== true) issues.push(issue("theme_rights_missing", `Bekreft kommersielle rettigheter for ${motif.label || "motivet"}.`));
  }
  return issues;
}

function theme(value, label, category, elementCount, filename, extras = {}) {
  return {
    value,
    label,
    category,
    elementCount,
    imageUrl: `/wall-decor/${filename}-thumb.webp`,
    thumbnailUrl: `/wall-decor/${filename}-thumb.webp`,
    productionAssetUrl: `/wall-decor/${filename}-print.webp`,
    productionWidth: 4096,
    productionHeight: 2731,
    commercialRightsConfirmed: true,
    rightsNote: "Originalt motiv laget for Navnlapp.no – ingen lisensierte figurer eller varemerker.",
    ...extras,
  };
}

function issue(code, message) {
  return { code, message };
}
